import * as React from "react";
import { Helmet } from "react-helmet";

interface HomePageProps {}

interface HomePageState {}

const CLIENTS = [
  "Australian Federal Police",
  "National Capital Authority",
  "Commonwealth Director of Public Prosecutions",
  "Australian Centre for International Agricultural Research",
  "Australian Communications and Media Authority (ACMA)",
  "Department of Climate Change, Energy, the Environment and Water",
  "Department of Agriculture, Fisheries and Forestry",
];

export default class HomePage extends React.Component<HomePageProps, HomePageState> {
  componentDidMount() {
    document.title = "Acader | AI & Digital Solutions";
  }

  render() {
    return (
      <div className="ai-home">
        <Helmet>
          <meta charSet="utf-8" />
          <title>Acader | AI & Digital Solutions</title>
          <meta
            name="description"
            content="Acader builds production-grade AI and software for government and enterprise — intelligent automation, custom platforms, and digital solutions that scale."
          />
        </Helmet>

        {/* ───────────── Hero ───────────── */}
        <section className="ai-hero">
          <div className="ai-hero__glow" />
          <div className="ai-hero__grid" />
          <div className="container ai-hero__inner">
            <span className="ai-pill">
              <span className="ai-pill__dot" />
              Digital &amp; AI Solutions
            </span>
            <h1 className="ai-hero__title">
              Intelligence that <span className="ai-gradient-text">ships.</span>
            </h1>
            <p className="ai-hero__subtitle">
              Acader designs and builds production-grade AI and software for government and enterprise — from intelligent automation to custom platforms
              engineered to scale.
            </p>
            <div className="ai-hero__actions">
              <a href="https://darci.ai" target="_blank" rel="noreferrer" className="ai-btn ai-btn--primary">
                Explore DARCI.AI
                <i className="fas fa-arrow-right ms-2" />
              </a>
              <a href="#contactus" className="ai-btn ai-btn--ghost">
                Talk to us
              </a>
            </div>
            <p className="ai-hero__trust">Trusted by Australian federal government agencies</p>
          </div>
        </section>

        {/* ───────────── Stats ───────────── */}
        <section className="ai-stats">
          <div className="container">
            <div className="row text-center">
              <div className="col-6 col-md-3 ai-stat">
                <div className="ai-stat__num ai-gradient-text">8+</div>
                <div className="ai-stat__label">Years delivering</div>
              </div>
              <div className="col-6 col-md-3 ai-stat">
                <div className="ai-stat__num ai-gradient-text">15+</div>
                <div className="ai-stat__label">Government agencies</div>
              </div>
              <div className="col-6 col-md-3 ai-stat">
                <div className="ai-stat__num ai-gradient-text">99.9%</div>
                <div className="ai-stat__label">Platform uptime</div>
              </div>
              <div className="col-6 col-md-3 ai-stat">
                <div className="ai-stat__num ai-gradient-text">24/7</div>
                <div className="ai-stat__label">Support &amp; monitoring</div>
              </div>
            </div>
          </div>
        </section>

        {/* ───────────── Services ───────────── */}
        <section className="ai-section" id="services">
          <div className="container">
            <div className="ai-section__head text-center">
              <span className="ai-eyebrow">What we do</span>
              <h2 className="ai-section__title">AI-first capabilities, end to end</h2>
              <p className="ai-section__lead">
                We combine applied AI, modern engineering, and disciplined delivery to turn ambitious ideas into systems you can rely on.
              </p>
            </div>
            <div className="row g-4">
              <div className="col-md-6 col-lg-3">
                <div className="ai-card">
                  <div className="ai-card__icon">
                    <i className="fas fa-robot" />
                  </div>
                  <h5 className="ai-card__title">
                    <a href="https://darci.ai" target="_blank" rel="noreferrer">
                      AI Solutions
                    </a>
                  </h5>
                  <p className="ai-card__text">
                    Self-driven agents, intelligent automation, and LLM-powered products that work in production — not just demos.
                  </p>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="ai-card">
                  <div className="ai-card__icon">
                    <i className="fas fa-code" />
                  </div>
                  <h5 className="ai-card__title">Software Engineering</h5>
                  <p className="ai-card__text">Custom platforms and APIs built with modern stacks, crafted to surpass security and performance standards.</p>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="ai-card">
                  <div className="ai-card__icon">
                    <i className="fas fa-project-diagram" />
                  </div>
                  <h5 className="ai-card__title">Project Delivery</h5>
                  <p className="ai-card__text">Agile, outcome-focused project management that keeps complex initiatives on time and on budget.</p>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="ai-card">
                  <div className="ai-card__icon">
                    <i className="fas fa-shield-alt" />
                  </div>
                  <h5 className="ai-card__title">Analysis &amp; Assurance</h5>
                  <p className="ai-card__text">Rigorous analysis, testing, and quality assurance for optimal performance and dependable outcomes.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ───────────── Mission / Why ───────────── */}
        <section className="ai-section ai-section--mission" id="aboutus">
          <div className="container">
            <div className="row align-items-center g-5">
              <div className="col-lg-6">
                <span className="ai-eyebrow">Our mission</span>
                <h2 className="ai-section__title text-start">Empowering businesses to move at the speed of AI</h2>
                <p className="ai-section__lead text-start">
                  We are a Canberra-based digital solutions provider on a mission to help organisations grow and adapt in an ever-evolving digital landscape —
                  with technology that is innovative, tailored, and built to last.
                </p>
                <ul className="ai-checklist">
                  <li>
                    <i className="fas fa-check" /> Streamlined processes and measurable productivity gains
                  </li>
                  <li>
                    <i className="fas fa-check" /> A competitive edge through innovation and adaptability
                  </li>
                  <li>
                    <i className="fas fa-check" /> Strategic growth, expanded reach, and stronger customer connections
                  </li>
                </ul>
              </div>
              <div className="col-lg-6">
                <div className="ai-orb">
                  <i className="fas fa-brain" />
                  <div className="ai-orb__ring ai-orb__ring--1" />
                  <div className="ai-orb__ring ai-orb__ring--2" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ───────────── Clients ───────────── */}
        <section className="ai-section ai-section--alt" id="clients">
          <div className="container">
            <div className="ai-section__head text-center">
              <span className="ai-eyebrow">Our clients</span>
              <h2 className="ai-section__title">Trusted across the public sector</h2>
              <p className="ai-section__lead">Delivering for diverse federal government needs, from regulators to research bodies.</p>
            </div>
          </div>
          {/* full-bleed continuously-scrolling marquee */}
          <div className="ai-marquee" aria-label="Our clients">
            <div className="ai-marquee__track">
              {/* render the list twice for a seamless loop */}
              {[...CLIENTS, ...CLIENTS].map((name, i) => (
                <div className="ai-client" key={`${name}-${i}`} aria-hidden={i >= CLIENTS.length}>
                  <img src="/images/australian-govt.png" alt="Australian Government" />
                  <hr />
                  <h5>{name}</h5>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ───────────── CTA ───────────── */}
        <section className="ai-cta" id="contactus">
          <div className="ai-cta__glow" />
          <div className="container">
            <div className="ai-cta__panel text-center">
              <span className="ai-eyebrow">Contact us</span>
              <h2 className="ai-cta__title">Let&apos;s build something intelligent</h2>
              <p className="ai-cta__lead">Reach out to learn how Acader can put AI and modern software to work for your organisation.</p>
              <a href="mailto:info@acader.com.au" className="ai-btn ai-btn--primary ai-btn--lg">
                <i className="fas fa-paper-plane me-2" />
                info@acader.com.au
              </a>
            </div>
          </div>
        </section>
      </div>
    );
  }
}
